<script>
	import {
		changeLabelLocale,
		checkTask,
		isSameId,
		menuIsEnabled,
	} from "@xbs/lib-todo";
	import { createEventDispatcher, onMount, getContext } from "svelte";

	import Menu from "../Menu/Menu.svelte";

	import Checkbox from "../../library/Checkbox.svelte";
	import Icon from "../../library/Icon.svelte";
	import Button from "../../library/Button.svelte";
	import Avatar from "../../library/Avatar.svelte";

	import Text from "./components/Text.svelte";
	import Editor from "./components/Editor.svelte";
	import DueDate from "./components/DueDate.svelte";
	import Priority from "./components/Priority.svelte";

	const dispatch = createEventDispatcher();

	const _ = getContext("wx-i18n").getGroup("todo");

	export let store;
	export let api;
	export let task;
	export let level = 0;

	let listNode = null;
	let userMenuNode = null;
	let taskMenuNode = null;

	const state = api.getReactiveState();
	const {
		stateDueDate: rDueDate,
		stateEditableItem: rEditor,
		menuState: rMenuState,
		filter: rFilter,
		tags: rTags,
		taskShape: rShape,
		readonly: rReadonly,
		priorityMap: rPriority,
		menu: rMenu,
	} = state;

	$: editor = $rEditor;
	$: menuState = $rMenuState;
	$: filter = $rFilter;
	$: tags = $rTags;
	$: shape = $rShape;
	$: readonly = $rReadonly;
	$: dueDate = $rDueDate;
	$: priority = $rPriority;
	$: menu = $rMenu;

	$: options = changeLabelLocale(
		menuState?.options?.length ? menuState?.options : [],
		_
	);
	$: availableUsers = changeLabelLocale(task?.availableUsers, _);

	$: hasChildren = task?.children?.length;
	$: initialPadding = hasChildren ? 16 : 40;
	$: paddingLeft = initialPadding + (level > 1 ? 24 * (level - 1) : 0);

	$: editorOpen = editor?.type === "task" && isSameId(editor?.id, task.id);

	task.selected && onMount(() => listNode?.focus());

	function triggerChecked() {
		if (task?.checked) {
			api.exec("uncheck-task", { id: task.id });
		} else {
			checkTask({ store, id: task.id });
		}
	}

	function triggerToggle() {
		dispatch("action", {
			action: task.collapsed ? "expand-task" : "collapse-task",
			data: { id: task.id },
		});
	}

	function handleEditor(event) {
		const { value, dropdown, targetDate } = event.detail;
		dispatch("action", {
			action: "edit-item",
			data: {
				id: task.id,
				currentValue: value,
				dropdown,
				targetDate,
			},
		});
	}

	function handleTaskMenuClick(event) {
		const action = event.detail.action?.id ?? event.detail.action;

		if (!action) {
			api.exec("close-menu");
		} else {
			dispatch("action", {
				action: "click-menu-item",
				data: {
					id: menuState.id,
					action,
					type: "task",
				},
			});
		}
	}

	$: showMenu = menuIsEnabled(menu, {
		store: api,
		type: "task",
		source: [task.id],
	});

</script>

<!-- svelte-ignore a11y-no-noninteractive-tabindex -->
<div
	tabindex="0"
	data-list-level={level}
	data-list-id={task.id}
	data-drag-list-id={task.id}
	class="wx-todo_list"
	class:wx-todo_list--selected={task.selected && !editorOpen}
	class:wx-todo_list--draggable={task.draggable && !editorOpen}
	style="padding-left:{paddingLeft}px;"
	bind:this={listNode}>
	<div class="wx-todo_list__controls wx-todo_list__controls-manual">
		{#if hasChildren}
			<div class="wx-todo_list-toggle-button">
				<Button circle={true} on:click={triggerToggle}>
					<Icon name={task.collapsed ? 'menu-right' : 'menu-down'} />
				</Button>
			</div>
		{/if}
		<Checkbox checked={task.checked} on:change={triggerChecked} />
	</div>
	<div class="wx-todo_list__content" data-list-editor-id={task.id}>
		{#if priority[task.priority]}
			<Priority {priority} {task} config={shape?.priority} />
		{/if}
		<div class="wx-todo_list__text">
			{#if editorOpen && !readonly}
				<Editor
					value={task.text || ''}
					placeholder={_('Type what you want')}
					{shape}
					{editor}
					{tags}
					on:editing={handleEditor} />
			{:else}
				<Text
					value={task.text || ''}
					completed={task.checked}
					{filter} />
			{/if}
		</div>
		<DueDate
			id={task.id}
			open={dueDate?.open && isSameId(task.id, dueDate?.id)}
			format={shape?.date?.format}
			validate={shape?.date?.validate}
			completed={task.checked}
			value={task.due_date}
			{readonly}
			on:action />
	</div>
	<div class="wx-todo_list__controls">
		{#if hasChildren}
			<div class="wx-todo_list__counter">
				{#if task.counter.type === 'number'}
					{task.counter.done}/{task.counter.total}
				{:else if task.counter.type === 'percentage'}
					{Math.round((task.counter.done / task.counter.total) * 100)}%
				{/if}
			</div>
		{/if}
		{#if availableUsers?.length}
			<div
				class="wx-todo_user-menu"
				style="width:{availableUsers.length >= 3 ? 60 : 16 * availableUsers.length + 14}px;"
				data-user-menu-id={task.id}
				bind:this={userMenuNode}>
				{#each availableUsers as user, index}
					{#if (availableUsers.length === 3 && index < 3) || index < 2}
						<div
							class="wx-todo_user-menu__avatar"
							style="left:{index * 16}px">
							<Avatar color={user.color} avatar={user.avatar} />
						</div>
					{/if}
				{/each}
				{#if availableUsers.length > 3}
					<div class="wx-todo_user-menu__avatar" style="left: 32px;">
						<Avatar value={`+${availableUsers.length - 2}`} />
					</div>
				{/if}
			</div>
		{/if}
		{#if !readonly && showMenu}
			<button
				bind:this={taskMenuNode}
				data-menu-id={task.id}
				class="wx-todo_list-menu-button">
				<Icon name="dots-v" />
			</button>
		{/if}
	</div>
</div>

{#if menuState && isSameId(menuState.id, task.id)}
	{#if menuState.type === 'user'}
		<Menu {api} options={availableUsers} parent={userMenuNode} />
	{/if}
	{#if menuState.type === 'task'}
		<Menu
			{api}
			{options}
			parent={taskMenuNode}
			on:click={handleTaskMenuClick} />
	{/if}
{/if}

<style>
	.wx-todo_list {
		position: relative;
		display: flex;
		justify-content: space-between;

		outline: none;
		padding: 0 16px;

		cursor: pointer;
	}
	.wx-todo_list--selected {
		background: var(--wx-background-selected);
	}
	.wx-todo_list--draggable {
		opacity: 0.4;
		background: var(--wx-background-selected);
	}
	.wx-todo_list__content {
		display: flex;
		align-items: flex-start;
		flex-direction: column;

		width: 100%;
		padding: 8px 6px;
	}
	.wx-todo_list__controls {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;

		z-index: 1;
	}
	.wx-todo_list__controls-manual {
		padding: 8px 0;
	}
	.wx-todo_list__text {
		height: 100%;
		width: 100%;

		min-height: var(--wx-line-height);

		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		line-height: var(--wx-line-height);

		color: var(--wx-color-font);
		user-select: none;

		z-index: 1;
	}
	.wx-todo_list__counter {
		color: var(--wx-color-secondary-light);

		margin: 8px 0 8px 6px;
		font-size: var(--wx-font-size-s);
		font-weight: var(--wx-font-weight-medium);
	}
	.wx-todo_list-menu-button {
		cursor: pointer;
		background: inherit;

		height: var(--wx-base-size);
		width: var(--wx-base-size);

		display: flex;
		align-items: center;
		justify-content: center;

		outline: none;
		border: none;
		border-radius: 50%;

		margin: 8px 0 8px 6px;
	}
	.wx-todo_list-menu-button:hover {
		background: var(--wx-icon-background-hover);
	}
	.wx-todo_list-toggle-button {
		display: flex;
		margin-right: 4px;
	}
	.wx-todo_user-menu {
		position: relative;
		height: 24px;

		display: flex;
		cursor: pointer;

		margin: 4px 0;
		margin-left: 6px;
	}
	.wx-todo_user-menu__avatar {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
	}
	:global(.wx-ondrag) .wx-todo_list,
	:global(.wx-ondrag) .wx-todo_user-menu {
		cursor: grabbing;
	}

</style>
